import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  startTransition,
} from "react";
import { useCall } from "../../call-sdk/context/CallContext";
import { useTwilioUserMediaContext } from "src/call-sdk/context/TwilioUserMediaContext";
import { localTrackToMediaStream } from "../../util/local-track.util";
import useVideoTrack from "../../hooks/useVideoTrack";
import RemoteParticipantVideo from "../remoteParticipantVideo/RemoteParticipantVideo";
import { SelfViewContainer } from "../selfView/VideoContainerStyles";
import { Fab, CircularProgress, Typography } from "@mui/material";
import CancelIcon from "../../assets/Room/cancel.svg";
import Video_Off from "../../assets/Room/video-off.svg";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useNavigate } from "react-router-dom";
import { useRoomContext } from "src/call-sdk/context/RoomContext";
import ErrorBoundary from "../ErrorBoundary";
import useParticipants from "src/hooks/useParticipants";
import {
  ConferanceRoomContainer,
  LocalVideo,
  ButtonContainer,
  HUViewContainer,
  IconContainer,
} from "./ConferanceRoomStyles";
import EventTracker from "src/pendo/EventTracker";
import StorageConstants from "src/config/storage.constants";
import InterpreterIcon from "../../assets/Room/interpreter-icon.svg";
import RemoteParticipantAudioOnly from "../RemoteParticipantAudioOnly/RemoteParticipantAudioOnly";
import LocalStorage from "src/call-sdk/lib/local-storage";
import AddinCallIcon from "../../assets/Room/add-call.svg";
import HuQRDisplay from "../HuQRDisplay/HuQRDisplay";
import useQRHuEnabled from "src/hooks/useQRHuEnabled";

const ConferanceRoom = () => {
  const {
    vco,
    hangup,
    callEnded,
    business,
    requestId,
    showHuQRDialog,
    toggleHuQRDialog,
  } = useCall();
  const { destroyMedia } = useTwilioUserMediaContext();
  const { room } = useRoomContext();
  const { participants, hearingParticipants } = useParticipants(
    room,
    requestId
  );
  const videoTrack = useVideoTrack();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [startAnimation, setStartAnimation] = useState(false);

  const qrHUConnectEnabled = useQRHuEnabled(business);

  useEffect(() => {
    setTimeout(() => {
      setStartAnimation(true);
    }, 3000);
  }, []);

  const isHUUser = Boolean(LocalStorage.get(StorageConstants.IS_HU_USER));

  useEffect(() => {
    let wakeLock: WakeLockSentinel | null = null;
    if ("wakeLock" in navigator) {
      const requestWakeLock = async () => {
        try {
          wakeLock = await navigator.wakeLock.request("screen");
          console.log("Wake lock is active");
        } catch (err) {
          console.error("Failed to acquire wake lock:", err);
        }
      };

      document.addEventListener("visibilitychange", () => {
        if (wakeLock !== null && document.visibilityState === "visible") {
          requestWakeLock();
        }
      });

      // Request the wake lock when the page loads
      requestWakeLock();
    }

    return () => {
      if (wakeLock !== null) {
        wakeLock.release().then(() => {});
        wakeLock = null;
        console.log("Wake Lock is released");
      }
    };
  }, []);

  const handleHangup = useCallback(() => {
    if (room) {
      room.localParticipant.videoTracks.forEach((video) => {
        if (!video.track.isEnabled) {
          video.track.enable();
        }
      });
      hangup();
      EventTracker.setAccountData(null);
      room.disconnect();
    }
  }, [room, hangup]);

  useEffect(() => {
    if (callEnded) {
      destroyMedia();
      navigate({ pathname: "/qr-feedback" }, { replace: true });
    }
  }, [callEnded, destroyMedia, navigate]);

  useEffect(() => {
    if (room) {
      room.localParticipant.audioTracks.forEach((publication) => {
        if (isHUUser || vco) {
          publication.track.enable();
        } else {
          publication.track.disable();
        }
      });
    }
  }, [room, vco, isHUUser]);

  const handleIncomingMessage = useCallback((text: string) => {
    console.log("Interpreter Message: ", text);
  }, []);

  const localVideoElement = useMemo(() => {
    if (videoTrack?.isEnabled) {
      return (
        <SelfViewContainer>
          <LocalVideo
            mediaStream={localTrackToMediaStream(videoTrack)}
            className="local-video-container"
            resize
          />
        </SelfViewContainer>
      );
    } else {
      return (
        <SelfViewContainer>
          <img src={Video_Off} alt="No Camera" style={{ height: "8vh" }} />
        </SelfViewContainer>
      );
    }
  }, [videoTrack]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 3000);

    // Log when participants are added (debugging)
    console.log("Waiting for participants...");

    if (participants.length > 0) {
      console.log("Participants are available now.");
      clearTimeout(timer);
      setLoading(false);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [participants, setLoading]);

  // useEffect(() => {
  //   if (hearingParticipants.length > 0) {
  //     toggleHuQRDialog(false);
  //   }
  // }, [hearingParticipants]);

  const participantVideos = useMemo(() => {
    if (loading) {
      return (
        <div className={"remote-video-container"}>
          <CircularProgress size={100} color="success" />
        </div>
      );
    } else if (participants.length > 0) {
      return (
        <div
          className={
            participants.length > 1
              ? "team-video-container"
              : "remote-video-container"
          }
        >
          {participants.map((participant) => (
            <RemoteParticipantVideo
              key={participant.sid}
              participant={participant}
              onMessage={handleIncomingMessage}
              viewStyle={
                participants.length > 1 ? "viewStyleTeam" : "viewStyleSingle"
              }
              muteSpeaker={!isHUUser && hearingParticipants.length > 0}
            />
          ))}
        </div>
      );
    } else {
      return (
        <div className={"remote-video-container"}>
          <p>No video available</p>
        </div>
      );
    }
  }, [
    participants,
    handleIncomingMessage,
    loading,
    hearingParticipants,
    isHUUser,
  ]);

  const participantAudioOnly = useMemo(() => {
    if (participants.length > 0) {
      return (
        <div
          className={
            participants.length > 1
              ? "team-video-container"
              : "remote-video-container"
          }
        >
          {participants.map((participant) => (
            <RemoteParticipantAudioOnly
              key={participant.sid}
              participant={participant}
            />
          ))}
        </div>
      );
    }
  }, [participants]);

  const RemoteHearingParticipant = useMemo(() => {
    if (hearingParticipants.length > 0) {
      return (
        <div>
          {hearingParticipants.map((participant) => (
            <RemoteParticipantAudioOnly
              key={participant.sid}
              participant={participant}
              muteSpeaker={true}
            />
          ))}
        </div>
      );
    }
  }, [hearingParticipants]);

  const HuUserView = useMemo(() => {
    return (
      <HUViewContainer>
        <IconContainer>
          <div
            className={`background-div ${startAnimation ? "large-div" : ""}`}
          >
            <LazyLoadImage
              className={`icon ${startAnimation ? "large" : ""}`}
              src={InterpreterIcon}
            />
          </div>
        </IconContainer>
        {<p className={`text ${startAnimation ? "hide" : ""}`}>Connected!</p>}
      </HUViewContainer>
    );
  }, [startAnimation]);

  return (
    <ErrorBoundary>
      <ConferanceRoomContainer>
        {isHUUser && HuUserView}

        {!isHUUser && localVideoElement}

        {isHUUser ? participantAudioOnly : participantVideos}
        {RemoteHearingParticipant}
        <ButtonContainer isDuUser={!isHUUser}>
          {!isHUUser && qrHUConnectEnabled && (
            <div>
              <Fab
                style={{
                  backgroundColor: `${
                    showHuQRDialog ? "rgba(0, 170, 85, 0.6)" : "#1D1D1D"
                  }`,
                }}
                className="cancelBtn"
                onClick={() => {
                  startTransition(() => {
                    toggleHuQRDialog(true);
                  });
                }}
                aria-label="Open Audio Connect"
              >
                <LazyLoadImage src={AddinCallIcon} />
              </Fab>
              <Typography className="end-text">Connect</Typography>
            </div>
          )}
          <div>
            <Fab
              style={{ backgroundColor: "red" }}
              className="cancelBtn"
              onClick={handleHangup}
              aria-label="End Call"
            >
              <LazyLoadImage src={CancelIcon} />
            </Fab>
            <Typography className="end-text">End</Typography>
          </div>
        </ButtonContainer>
        <HuQRDisplay
          open={showHuQRDialog}
          onClose={() => toggleHuQRDialog(false)}
        />
      </ConferanceRoomContainer>
    </ErrorBoundary>
  );
};

export default ConferanceRoom;
